import { G } from "../constants/theme";
import Label from "../components/ui/Label";
import BigTitle from "../components/ui/BigTitle";
import BodyText from "../components/ui/BodyText";
import { BtnG, BtnO } from "../components/ui/Buttons";
import { Wrap, Sec } from "../components/ui/Layout";
import StatBox from "../components/ui/StatBox";
import HLCard from "../components/ui/HLCard";
import AppCard from "../components/ui/AppCard";

export default function Home({ onNavigate }) {
  return (
    <>
      {/* Hero */}
      <div
        style={{
          background: `radial-gradient(ellipse 80% 70% at 75% 50%, rgba(76,175,80,.07), transparent 65%),
            linear-gradient(180deg, ${G.black} 0%, ${G.deep} 100%)`,
          padding: "80px 0 56px",
        }}
      >
        <Wrap>
          <Label>Industrial rCB Supply — Tire Pyrolysis</Label>
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(42px,9vw,114px)",
              lineHeight: 0.9,
              color: G.white,
              letterSpacing: 2,
              marginBottom: 28,
            }}
          >
            RECOVERED <span style={{ color: G.green }}>CARBON</span>
            <br />
            BLACK POWDER
          </h1>
          <p style={{ fontSize: 17, color: G.muted, maxWidth: 520, marginBottom: 40, lineHeight: 1.65 }}>
            Industrial-grade recovered carbon black powder produced from tire pyrolysis, available
            for bulk supply.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 60 }}>
            <BtnG onClick={() => onNavigate("technical")}>Request Technical Data</BtnG>
            <BtnO onClick={() => onNavigate("contact")}>Contact Our Team</BtnO>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 1,
              background: G.border,
              border: `1px solid ${G.border}`,
              maxWidth: 480,
            }}
          >
            <StatBox num="~1000" label="MT Available" />
            <StatBox num="rCB" label="Powder Form" />
            <StatBox num="B2B" label="Bulk Supply" />
          </div>
        </Wrap>
      </div>

      {/* Material overview */}
      <Sec bg={G.deep}>
        <Wrap>
          <Label>Material Overview</Label>
          <BigTitle>
            INDUSTRIAL RECOVERED
            <br />
            CARBON MATERIAL
          </BigTitle>
          <BodyText>
            Supplied in powder form for industrial applications where cost efficiency, material
            reuse, and bulk supply are key considerations.
          </BodyText>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              border: `1px solid ${G.border}`,
              gap: 1,
              background: G.border,
            }}
          >
            <HLCard
              icon="◈"
              title="Recovered Carbon Black Powder"
              desc="Supplied in powder form, ready for industrial processing and evaluation."
            />
            <HLCard
              icon="⌬"
              title="Tire Pyrolysis Origin"
              desc="Produced from end-of-life tire pyrolysis operations based in Syria."
            />
            <HLCard
              icon="▣"
              title="~1,000 MT Available"
              desc="Approximately 1,000 metric tons currently available for bulk purchase."
            />
            <HLCard
              icon="◎"
              title="Samples on Request"
              desc="Laboratory samples available to qualified buyers for application testing."
            />
          </div>
        </Wrap>
      </Sec>

      {/* Applications */}
      <Sec bg={G.black}>
        <Wrap>
          <Label>End Use</Label>
          <BigTitle>
            SUITABLE INDUSTRIAL
            <br />
            APPLICATIONS
          </BigTitle>
          <BodyText>
            Our material can be evaluated for industrial filler, plastics, selected rubber, and
            energy applications.
          </BodyText>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 1,
              background: G.border,
              border: `1px solid ${G.border}`,
            }}
          >
            <AppCard
              num="01"
              title="Industrial Fillers"
              desc="General-purpose filler material for industrial compounds and manufacturing."
            />
            <AppCard
              num="02"
              title="Plastics & Polymers"
              desc="Black filler or coloring material in plastic and polymer processing."
            />
            <AppCard
              num="03"
              title="Rubber Applications"
              desc="Non-critical rubber products and compound evaluation."
            />
            <AppCard
              num="04"
              title="Energy & Fuel"
              desc="Energy recovery and industrial fuel-related applications."
            />
          </div>
        </Wrap>
      </Sec>

      {/* Origin strip */}
      <div
        style={{
          background: G.surface,
          borderTop: `1px solid ${G.border}`,
          borderBottom: `1px solid ${G.border}`,
          padding: "52px 0",
        }}
      >
        <Wrap>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1px 1fr",
              gap: 48,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 9,
                  letterSpacing: 3,
                  color: G.muted,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Production
              </div>
              <div
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 42,
                  color: G.white,
                  letterSpacing: 3,
                }}
              >
                SYRIA
              </div>
              <div style={{ fontSize: 13, color: G.muted, marginTop: 5 }}>
                Tire pyrolysis facility &amp; operations
              </div>
            </div>
            <div style={{ height: 60, background: G.border, position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                  color: G.green,
                  background: G.surface,
                  padding: "4px 8px",
                  fontSize: 16,
                }}
              >
                ↔
              </span>
            </div>
            <div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 9,
                  letterSpacing: 3,
                  color: G.muted,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Commercial Contact
              </div>
              <div
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 42,
                  color: G.white,
                  letterSpacing: 3,
                }}
              >
                CANADA
              </div>
              <div style={{ fontSize: 13, color: G.muted, marginTop: 5 }}>
                Buyer communication &amp; coordination
              </div>
            </div>
          </div>
        </Wrap>
      </div>

      {/* CTA */}
      <Sec bg={G.deep} style={{ textAlign: "center" }}>
        <Wrap>
          <Label>Next Step</Label>
          <BigTitle>
            INTERESTED IN TECHNICAL
            <br />
            DATA OR SAMPLES?
          </BigTitle>
          <BodyText center>
            Contact us to request lab analysis, samples, or bulk supply information.
          </BodyText>
          <BtnG onClick={() => onNavigate("contact")}>Request Information</BtnG>
        </Wrap>
      </Sec>
    </>
  );
}
